import type { TOC } from '@ember/component/template-only';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import {
  PrismBanner,
  PrismButton,
  PrismCard,
  PrismComboSelectInput,
  PrismInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralNavigationIntercomHelpLink from 'lumen/components/general/navigation/intercom-help-link';
import SettingsMyotbOtbSelfServiceDataBar from 'lumen/components/settings/myotb/otb-self-service-data-bar';
import type HotelSettingsMyotbOtherSystemSelectionController from 'lumen/controllers/hotel/settings/myotb/other-system-selection';

interface HotelSettingsMyotbOtherSystemSelectionSignature {
  Args: {
    controller: HotelSettingsMyotbOtherSystemSelectionController;
    model: unknown;
  };
}

<template>
  <PrismCard class="myotb-card">
    <:header as |Header|>
      <div class="grid gap-400">
        <SettingsMyotbOtbSelfServiceDataBar />
        <Header.title>
          {{t "settings.my_otb.other_systems.title"}}
        </Header.title>
      </div>
    </:header>

    <:content>
      <div
        class="myotb-card__content-container flex flex-col gap-400"
        {{didInsert @controller.setDefaults}}
      >
        {{#if @controller.isOtherSystemPrefilled}}
          <PrismBanner
            @bannerMessage={{t
              "settings.my_otb.other_systems.support_through_system.body"
            }}
            @bannerTitle={{t
              "settings.my_otb.other_systems.support_through_system.title"
            }}
          />
        {{else}}
          <PrismBanner
            @bannerMessage={{t
              "settings.my_otb.other_systems.pms_not_supported.body"
            }}
            @bannerTitle={{t
              "settings.my_otb.other_systems.pms_not_supported.title"
            }}
          />
        {{/if}}

        <div class="flex flex-col gap-100">
          <label class="text-200--bold">
            {{t "settings.my_otb.other_systems.body"}}
          </label>
          <div class="myotb-selection-input-container flex flex-col">
            <PrismComboSelectInput
              @isSearchable={{true}}
              @isSecondary={{true}}
              @onChange={{@controller.onSelectOtherSystem}}
              @options={{@controller.otherSystemSelectionOptions}}
              @placeholder={{t
                "settings.my_otb.other_systems.search_supported"
              }}
              @searchMatcher={{@controller.pmsAndOtherSystemSearchMatcher}}
              @value={{@controller.selectedOtherSystem}}
            />
          </div>
        </div>

        {{#if @controller.hasSelectedNoneOfTheAbove}}
          <div class="flex flex-col gap-100">
            <label class="text-200--bold">
              {{t "settings.my_otb.other_systems.system_name"}}
            </label>
            <div class="myotb-selection-input-container flex flex-col">
              <PrismInput
                @onInput={{@controller.updateCustomOtherSystemName}}
                @removePadding={{true}}
                @value={{@controller.customOtherSystemName}}
              />
            </div>
          </div>
        {{/if}}

        <GeneralNavigationIntercomHelpLink
          @articleLink="en/articles/6344009-my-otb-other-systems"
          @iconName="link-out"
          @isStandaloneLink={{true}}
          @linkText={{t "settings.my_otb.other_systems.learn_more_link"}}
          @type="text-and-icon"
        />
      </div>
    </:content>

    <:footer>
      <div class="flex justify-between gap-300">
        <PrismButton
          @buttonType="ghost"
          @iconBefore="arrow-left"
          @label={{t "general.back"}}
          @onClick={{@controller.onClickPrevious}}
        />
        <PrismButton
          @iconAfter="arrow-right"
          @isDisabled={{@controller.isContinueDisabled}}
          @isLoading={{@controller.isLoading}}
          @label={{t "general.continue"}}
          @onClick={{@controller.onClickContinue}}
        />
      </div>
    </:footer>
  </PrismCard>
</template> satisfies TOC<HotelSettingsMyotbOtherSystemSelectionSignature>;
